import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const FlagTransactionModal = ({ isOpen, onClose, transaction }) => {
  const [flagReason, setFlagReason] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [priority, setPriority] = useState('medium');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  if (!isOpen || !transaction) return null;

  const handleSubmitFlag = async () => {
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setSuccessMessage('Transaction flagged successfully. Compliance team has been notified.');
    setIsSubmitting(false);
    
    // Auto-close after success
    setTimeout(() => {
      setSuccessMessage('');
      setFlagReason('');
      setAdditionalNotes('');
      setPriority('medium');
      onClose();
    }, 2500);
  };

  const flagReasons = [
    { value: '', label: 'Select a reason...' },
    { value: 'suspicious_activity', label: 'Suspicious Activity' },
    { value: 'unusual_amount', label: 'Unusual Amount' },
    { value: 'identity_verification', label: 'Identity Verification Required' },
    { value: 'document_issue', label: 'Document Issue' },
    { value: 'customer_behavior', label: 'Concerning Customer Behavior' },
    { value: 'compliance_concern', label: 'Compliance Concern' },
    { value: 'fraud_suspicion', label: 'Potential Fraud' },
    { value: 'other', label: 'Other (Specify in notes)' }
  ];

  const priorityLevels = [
    { value: 'low', label: 'Low Priority', color: 'text-info' },
    { value: 'medium', label: 'Medium Priority', color: 'text-warning' },
    { value: 'high', label: 'High Priority', color: 'text-error' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-border flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-foreground flex items-center space-x-2">
              <Icon name="Flag" size={24} className="text-warning" />
              <span>Flag Transaction for Review</span>
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Transaction: {transaction?.id}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {successMessage ? (
            <div className="bg-success/10 border border-success/20 rounded-lg p-4">
              <div className="flex items-center space-x-2 text-success">
                <Icon name="CheckCircle" size={20} />
                <span className="font-medium">{successMessage}</span>
              </div>
            </div>
          ) : (
            <>
              <div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
                <div className="flex items-center space-x-2 text-warning mb-1">
                  <Icon name="AlertTriangle" size={16} />
                  <span className="font-medium">Important Notice</span>
                </div>
                <p className="text-xs text-warning/80">
                  Flagging this transaction will put it under compliance review. The customer and recipient 
                  will be notified that additional verification is required. Funds may be temporarily held 
                  until review is complete.
                </p>
              </div>

              <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                <h3 className="text-sm font-semibold text-foreground">Transaction Summary</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground">Customer</div>
                    <div className="font-medium text-foreground">{transaction?.customer}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Recipient</div>
                    <div className="font-medium text-foreground">{transaction?.recipient}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Amount</div>
                    <div className="font-medium text-foreground">
                      {transaction?.sendAmount} {transaction?.fromCurrency} → {transaction?.receiveAmount} {transaction?.toCurrency}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Date</div>
                    <div className="font-medium text-foreground">
                      {new Date(transaction?.date)?.toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">
                  Reason for Flagging <span className="text-error">*</span>
                </label>
                <Select
                  value={flagReason}
                  onChange={(e) => setFlagReason(e?.target?.value)}
                  options={flagReasons}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">
                  Priority Level <span className="text-error">*</span>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {priorityLevels?.map((level) => (
                    <button
                      key={level?.value}
                      onClick={() => setPriority(level?.value)}
                      className={`flex items-center justify-center p-3 rounded-lg border-2 transition-all ${
                        priority === level?.value
                          ? 'border-primary bg-primary/10' :'border-border bg-background hover:bg-muted'
                      }`}
                    >
                      <span className={`text-sm font-medium ${
                        priority === level?.value ? 'text-primary' : level?.color
                      }`}>
                        {level?.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">
                  Additional Notes <span className="text-error">*</span>
                </label>
                <textarea
                  value={additionalNotes}
                  onChange={(e) => setAdditionalNotes(e?.target?.value)}
                  placeholder="Provide detailed information about why this transaction requires review..."
                  rows={3}
                  className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
                <div className="text-xs text-muted-foreground">
                  Be specific and include all relevant details. Minimum 20 characters.
                </div>
              </div>

              <div className="bg-info/10 border border-info/20 rounded-lg p-3">
                <div className="flex items-center space-x-2 text-info mb-1">
                  <Icon name="Info" size={16} />
                  <span className="font-medium">What happens next?</span>
                </div>
                <ul className="text-xs text-info/80 space-y-0.5 ml-4 list-disc">
                  <li>Compliance team will review within 24-48 hours</li>
                  <li>Customer will be notified of additional verification requirements</li>
                  <li>Transaction may be temporarily held pending review</li>
                  <li>You'll receive updates via dashboard notifications</li>
                </ul>
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t border-border flex justify-end space-x-3 flex-shrink-0">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmitFlag}
            disabled={!flagReason || !additionalNotes?.trim() || additionalNotes?.length < 20 || isSubmitting || successMessage}
            className="bg-warning hover:bg-warning/90"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Submitting...
              </>
            ) : (
              <>
                <Icon name="Flag" size={16} className="mr-2" />
                Submit Flag
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FlagTransactionModal;